import { useState, type ReactNode } from 'react';

import {
    Card,
    Icon,
    MonoLabel,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
    cn,
    costValueColor,
} from '@/design-system';
import {
    canAdjustCosts,
    findingsBesideNhgRows,
    findingsOfNhgRow,
    nhgRowOptions,
    type Report,
    type ReportFinding,
    type ReportFindingAdjustmentInput,
    type ReportNhgRow,
    type ReportNhgTable,
} from '@/entities/report';
import { formatCurrency } from '@/shared/lib';

import { reportCopy } from '../copy';
import { reportProblemMessage } from '../useReportScreen';
import { AdjustFindingDrawer } from './AdjustFindingDrawer';
import { CostFindingCard } from './CostFindingCard';

export interface CostsPanelProps {
    report: Report;
    onAdjust: (finding: ReportFinding, input: ReportFindingAdjustmentInput) => Promise<unknown>;
    onConfirm: (finding: ReportFinding) => Promise<unknown>;
}

/** Een bedrag in een tabel; nul is een streepje, zoals in de NHG-bijlage. */
function amountOrDash(amount: number): string {
    return amount === 0 ? reportCopy.costs.noAmount : formatCurrency(amount);
}

/**
 * Kosten & NHG: hoe de bedragen van dit rapport in de NHG-bijlage komen, en de weg
 * terug van elke regel naar de bevindingen erachter.
 *
 * Bovenaan waar de kosten staan (in het rapport, in de bijlage, erbuiten), dan de
 * twee tabellen van het NHG-model zoals in de PDF. Een regel met bevindingen klapt
 * open; elke bevinding zegt hoe haar bedrag ontstond en wie het aanpaste. Aanpassen
 * gebeurt per bevinding in een paneel, zolang het een concept is en deze persoon het
 * mag; wie niet de inspecteur is, zet een aanpassing klaar die op akkoord wacht.
 */
export function CostsPanel({ report, onAdjust, onConfirm }: CostsPanelProps) {
    const copy = reportCopy.costs;
    const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
    const [editing, setEditing] = useState<ReportFinding | null>(null);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [problem, setProblem] = useState<unknown>(null);

    const adjustable = canAdjustCosts(report);
    const canConfirm = report.editable && !report.surveyOpen && report.can.confirm;
    const nhg = report.nhg;
    const beside = findingsBesideNhgRows(report);

    const intro = report.surveyOpen
        ? copy.introSurveyOpen
        : adjustable
          ? copy.introEditable
          : report.editable
            ? copy.introReadOnly
            : copy.introLocked;

    const toggle = (code: string) =>
        setOpen((current) => {
            const next = new Set(current);

            if (next.has(code)) {
                next.delete(code);
            } else {
                next.add(code);
            }

            return next;
        });

    const confirm = async (finding: ReportFinding) => {
        setBusyKey(finding.key);
        setProblem(null);
        setNotice(null);

        try {
            await onConfirm(finding);
            setNotice(copy.confirmed);
        } catch (caught) {
            setProblem(caught);
        } finally {
            setBusyKey(null);
        }
    };

    const card = (finding: ReportFinding) => (
        <CostFindingCard
            key={finding.key}
            finding={finding}
            canAdjust={adjustable}
            canConfirm={canConfirm}
            busy={busyKey !== null}
            onAdjust={(target) => {
                setNotice(null);
                setProblem(null);
                setEditing(target);
            }}
            onConfirm={(target) => void confirm(target)}
        />
    );

    return (
        <section aria-labelledby="costs-panel-title" className="mb-6 grid gap-5">
            <header>
                <MonoLabel size={10} tone="muted" as="div" className="tracking-[0.1em]">
                    {copy.eyebrow}
                </MonoLabel>
                <h4
                    id="costs-panel-title"
                    className="m-0 mt-[6px] font-[family-name:var(--font-display)] text-[18px] leading-[1.25] font-semibold text-[var(--text-primary)]"
                >
                    {copy.title}
                </h4>
                <p className="m-0 mt-[6px] max-w-[640px] text-[13px] leading-[1.55] text-[var(--text-secondary)]">
                    {intro}
                </p>
            </header>

            {notice ? (
                <p
                    role="status"
                    className="m-0 text-[13px] leading-[1.5] text-[var(--accent-dark)]"
                >
                    {notice}
                </p>
            ) : null}
            {problem ? <PanelProblem error={problem} /> : null}

            <WhereCostsAre report={report} />

            {nhg === null || !nhg.enabled ? (
                <Card variant="muted" radius={9} padding="md">
                    <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                        {copy.annexOff}
                    </p>
                </Card>
            ) : (
                <div className="grid gap-4">
                    <div>
                        <h5 className="m-0 font-[family-name:var(--font-display)] text-[15px] font-semibold text-[var(--text-primary)]">
                            {copy.annexTitle}
                        </h5>
                        <p className="m-0 mt-1 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                            {copy.annexNote}
                        </p>
                    </div>
                    {nhg.tables.map((table) => (
                        <NhgTable
                            key={table.key}
                            table={table}
                            open={open}
                            onToggle={toggle}
                            renderFindings={(row) => findingsOfNhgRow(report, row).map(card)}
                        />
                    ))}
                </div>
            )}

            {beside.outside.length > 0 ? (
                <BesideGroup
                    title={copy.outsideTitle(beside.outside.length)}
                    note={copy.outsideNote}
                    findings={beside.outside}
                    reasonOf={(finding) =>
                        finding.nhg?.outsideReason
                            ? (copy.outsideReason[finding.nhg.outsideReason] ?? null)
                            : null
                    }
                    renderCard={card}
                />
            ) : null}
            {beside.improvementOnly.length > 0 ? (
                <BesideGroup
                    title={copy.improvementTitle}
                    note={copy.improvementNote}
                    findings={beside.improvementOnly}
                    renderCard={card}
                />
            ) : null}
            {beside.withoutAmount.length > 0 ? (
                <BesideGroup
                    title={copy.withoutAmountTitle}
                    findings={beside.withoutAmount}
                    renderCard={card}
                />
            ) : null}

            {report.findings.length === 0 ? (
                <p className="m-0 text-[13px] text-[var(--text-tertiary-safe)]">{copy.empty}</p>
            ) : null}

            <AdjustFindingDrawer
                finding={editing}
                nhgRows={nhgRowOptions(report)}
                awaitsInspector={!report.can.confirm}
                onClose={() => setEditing(null)}
                onSubmit={async (finding, input) => {
                    await onAdjust(finding, input);
                    setEditing(null);
                    setNotice(copy.saved);
                }}
            />
        </section>
    );
}

/** De drie kostenblokken naast elkaar: in het rapport, in de bijlage en erbuiten. */
function WhereCostsAre({ report }: { report: Report }) {
    const copy = reportCopy.costs;
    const nhg = report.nhg;
    const rows: Array<{
        label: string;
        direct: number;
        termijn: number;
        onderhoud: number;
        caption?: string;
    }> = [{ label: copy.whereReport, ...report.costs }];

    if (nhg && nhg.enabled) {
        rows.push(
            {
                label: copy.whereAnnex,
                direct: nhg.total.direct,
                termijn: nhg.total.termijn,
                onderhoud: nhg.improvement,
            },
            {
                label: copy.whereOutside,
                direct: nhg.outside.direct,
                termijn: nhg.outside.termijn,
                onderhoud: nhg.outside.onderhoud,
                caption: copy.whereOutsideCount(nhg.outside.count),
            },
        );
    }

    return (
        <div>
            <h5 className="m-0 mb-2 font-[family-name:var(--font-display)] text-[15px] font-semibold text-[var(--text-primary)]">
                {copy.whereTitle}
            </h5>
            <div className="overflow-hidden rounded-[8px] border border-[var(--border-default)]">
                <Table
                    columns="minmax(150px,1.3fr) repeat(3, minmax(104px,1fr))"
                    minWidth={480}
                    label={copy.whereLabel}
                >
                    <TableHead sticky={false}>
                        <TableHeaderCell>
                            <span className="sr-only">{copy.whereTitle}</span>
                        </TableHeaderCell>
                        <TableHeaderCell align="end">{copy.columnDirect}</TableHeaderCell>
                        <TableHeaderCell align="end">{copy.columnTerm}</TableHeaderCell>
                        <TableHeaderCell align="end">{copy.columnImprovement}</TableHeaderCell>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.label}>
                                <TableCell emphasis>
                                    {row.label}
                                    {row.caption ? (
                                        <span className="mt-[2px] block text-[12px] font-normal text-[var(--text-tertiary-safe)]">
                                            {row.caption}
                                        </span>
                                    ) : null}
                                </TableCell>
                                {(['direct', 'termijn', 'onderhoud'] as const).map((key) => (
                                    <TableCell key={key} mono align="end">
                                        <span
                                            style={{
                                                color:
                                                    row[key] === 0
                                                        ? undefined
                                                        : costValueColor[key],
                                            }}
                                        >
                                            {formatCurrency(row[key])}
                                        </span>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

/**
 * Eén tabel van het NHG-model. Elke regel met bevindingen is een knop over de hele
 * breedte die de bevindingen erachter laat zien; een regel zonder bevindingen staat
 * er stil en gedempt bij, zoals op het formulier.
 */
function NhgTable({
    table,
    open,
    onToggle,
    renderFindings,
}: {
    table: ReportNhgTable;
    open: ReadonlySet<string>;
    onToggle: (code: string) => void;
    renderFindings: (row: ReportNhgRow) => ReactNode;
}) {
    const copy = reportCopy.costs;
    const columns = '64px minmax(170px,1.5fr) minmax(90px,1fr) 124px 124px 40px';

    return (
        <div className="overflow-hidden rounded-[8px] border border-[var(--border-default)]">
            <Table columns={columns} minWidth={620} label={table.label}>
                <TableHead sticky={false}>
                    <TableHeaderCell>{copy.columnCode}</TableHeaderCell>
                    <TableHeaderCell>{table.label}</TableHeaderCell>
                    <TableHeaderCell>{copy.columnLocations}</TableHeaderCell>
                    <TableHeaderCell align="end">{copy.columnNecessaryDirect}</TableHeaderCell>
                    <TableHeaderCell align="end">{copy.columnNecessaryTerm}</TableHeaderCell>
                    <TableHeaderCell>
                        <span className="sr-only">{copy.columnFindings}</span>
                    </TableHeaderCell>
                </TableHead>
                <TableBody>
                    {table.rows.map((row) => {
                        const filled = row.findingKeys.length > 0;
                        const expanded = filled && open.has(row.code);

                        return (
                            <NhgTableRow
                                key={row.code}
                                row={row}
                                filled={filled}
                                expanded={expanded}
                                onToggle={() => onToggle(row.code)}
                                findings={expanded ? renderFindings(row) : null}
                            />
                        );
                    })}
                    <TableRow className="bg-[var(--surface-subtle)]">
                        <TableCell />
                        <TableCell emphasis>{copy.tableTotal(table.label)}</TableCell>
                        <TableCell />
                        <TableCell mono align="end" className="font-medium">
                            {formatCurrency(table.total.direct)}
                        </TableCell>
                        <TableCell mono align="end" className="font-medium">
                            {formatCurrency(table.total.termijn)}
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
}

function NhgTableRow({
    row,
    filled,
    expanded,
    onToggle,
    findings,
}: {
    row: ReportNhgRow;
    filled: boolean;
    expanded: boolean;
    onToggle: () => void;
    findings: ReactNode;
}) {
    const copy = reportCopy.costs;
    const regionId = `nhg-row-${row.code.replaceAll('.', '-')}`;

    return (
        <>
            <TableRow
                selected={expanded}
                onSelect={filled ? onToggle : undefined}
                aria-expanded={filled ? expanded : undefined}
                aria-controls={filled && expanded ? regionId : undefined}
                aria-label={
                    filled
                        ? `${row.code} ${row.element}: ${copy.rowFindings(row.findingKeys.length)}`
                        : undefined
                }
                className={cn('min-h-[44px]', !filled && 'text-[var(--text-tertiary-safe)]')}
            >
                <TableCell mono muted={!filled}>
                    {row.code}
                </TableCell>
                <TableCell emphasis={filled} muted={!filled}>
                    {row.element}
                </TableCell>
                <TableCell muted>
                    {row.locations.length > 0 ? row.locations.join(', ') : copy.noAmount}
                </TableCell>
                <TableCell mono align="end" muted={!filled}>
                    <span style={{ color: row.direct === 0 ? undefined : costValueColor.direct }}>
                        {amountOrDash(row.direct)}
                    </span>
                </TableCell>
                <TableCell mono align="end" muted={!filled}>
                    <span style={{ color: row.termijn === 0 ? undefined : costValueColor.termijn }}>
                        {amountOrDash(row.termijn)}
                    </span>
                </TableCell>
                <TableCell align="end">
                    {filled ? (
                        <Icon
                            name="chevronDown"
                            size="sm"
                            tone="muted"
                            className={cn(expanded && 'rotate-180')}
                        />
                    ) : null}
                </TableCell>
            </TableRow>
            {expanded ? (
                <TableRow className="bg-[var(--surface-page)]">
                    <TableCell className="col-span-full p-3">
                        <div id={regionId} className="grid gap-[10px]">
                            {findings}
                        </div>
                    </TableCell>
                </TableRow>
            ) : null}
        </>
    );
}

/** Bevindingen die niet op een regel staan, met waarom, en even aan te passen. */
function BesideGroup({
    title,
    note,
    findings,
    reasonOf,
    renderCard,
}: {
    title: string;
    note?: string;
    findings: ReportFinding[];
    reasonOf?: (finding: ReportFinding) => string | null;
    renderCard: (finding: ReportFinding) => ReactNode;
}) {
    const [expanded, setExpanded] = useState(false);
    const id = `beside-${title.replace(/\W+/g, '-').toLowerCase()}`;

    return (
        <div className="rounded-[8px] border border-[var(--border-default)] bg-[var(--surface-card)]">
            <button
                type="button"
                aria-expanded={expanded}
                aria-controls={id}
                onClick={() => setExpanded((current) => !current)}
                className="flex min-h-[48px] w-full cursor-pointer items-center justify-between gap-3 rounded-[8px] border-0 bg-transparent px-[14px] py-[10px] text-left font-[family-name:var(--font-body)] outline-offset-2 hover:bg-[var(--surface-row-hover)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
            >
                <span className="min-w-0">
                    <span className="block text-[14px] font-medium text-[var(--text-primary)]">
                        {title}
                    </span>
                    {note ? (
                        <span className="mt-[2px] block text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                            {note}
                        </span>
                    ) : null}
                </span>
                <Icon
                    name="chevronDown"
                    size="sm"
                    tone="muted"
                    className={cn(expanded && 'rotate-180')}
                />
            </button>
            {expanded ? (
                <div id={id} className="grid gap-[10px] border-t border-[var(--border-subtle)] p-3">
                    {findings.map((finding) => {
                        const reason = reasonOf?.(finding) ?? null;

                        return (
                            <div key={finding.key} className="grid gap-[6px]">
                                {reason ? (
                                    <MonoLabel size={10} tone="muted" as="div">
                                        {reason}
                                    </MonoLabel>
                                ) : null}
                                {renderCard(finding)}
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}

function PanelProblem({ error }: { error: unknown }) {
    return (
        <p role="alert" className="m-0 text-[13px] leading-[1.5] text-[var(--danger)]">
            {reportProblemMessage(error)}
        </p>
    );
}
